import { Injectable } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import { persistState } from '@ngneat/elf-persist-state';
import { differenceInMinutes } from 'date-fns';
import { filter } from 'rxjs';
import { NavbarPosition } from '../api';
import { LogEntry } from '../logging';
import { NavbarItem, NavbarItemEnum } from '../models/app';
import { ensureDate } from '../utils';
import { capacitorStorageStrategy, includeKeys } from '../utils/elf-utils';
import { AccountChips, DevSettings, LaunchSettings, LogEntryInApp } from './app.models';

interface AppProps {
  launchSettings: LaunchSettings;
  devSettings: DevSettings;
  navBarComputed: boolean;
  navBarItems: NavbarItem[];
  skipLinkAccount: boolean;
  accountChips: AccountChips;
  logs: LogEntryInApp[];
  lifecycle: {
    pausedAt: Date | null;
    resumedAt: Date | null;
  };
}

const MAX_LOGS = 50;

export const defaultSurveyNavbarItem: NavbarItem = {
  svg: '/assets/svg/star.svg',
  key: NavbarItemEnum.Survey,
  path: '/account/chats',
  action: 'OPEN_SURVEY',
  isActivated: false,
  isOnNavBar: false,
  matchExactPath: false,
  notif: null,
};

@Injectable({ providedIn: 'root' })
export class AppRepository {
  private store = createStore(
    { name: 'app' },
    withProps<AppProps>({
      launchSettings: {
        isAppEmbedded: false,
        navBarPlace: NavbarPosition.Bottom,
      },
      navBarComputed: false,
      navBarItems: [
        {
          svg: '/assets/svg/home.svg',
          key: NavbarItemEnum.Home,
          path: '/restaurants',
          matchExactPath: false,
          isActivated: false,
          isOnNavBar: false,
          notif: null,
        },
        {
          svg: '/assets/svg/order.svg',
          key: NavbarItemEnum.Order,
          path: '/restaurants-with-order',
          matchExactPath: false,
          isActivated: false,
          isOnNavBar: false,
          notif: null,
        },
        {
          svg: '/assets/svg/reload.svg',
          key: NavbarItemEnum.Reload,
          path: '/reload',
          matchExactPath: false,
          isActivated: false,
          isOnNavBar: false,
          notif: null,
        },
        {
          svg: '/assets/svg/reservation.svg',
          key: NavbarItemEnum.Book,
          path: '/booking-home',
          matchExactPath: false,
          isActivated: false,
          isOnNavBar: false,
          notif: null,
        },
        {
          svg: '/assets/svg/receipt-filled.svg',
          key: NavbarItemEnum.History,
          path: '/history-home',
          matchExactPath: false,
          isActivated: false,
          isOnNavBar: false,
          notif: null,
        },
        { ...defaultSurveyNavbarItem },
        {
          svg: '/assets/svg/profil.svg',
          key: NavbarItemEnum.Account,
          path: '/account',
          isActivated: true,
          isOnNavBar: false,
          matchExactPath: false,
          notif: null,
        },
      ],
      devSettings: { debug: false, prefill: false, devOverlay: false, demoProxy: false },
      skipLinkAccount: false,
      accountChips: { notificationsCount: 0, newsCount: 0, chatsCount: 0 },
      logs: [],
      lifecycle: {
        pausedAt: null,
        resumedAt: null,
      },
    }),
  );

  instance = persistState(this.store, {
    key: 'app',
    storage: capacitorStorageStrategy,
    source: () => this.store.pipe(includeKeys(['launchSettings', 'devSettings', 'skipLinkAccount', 'logs', 'lifecycle', 'navBarItems'])),
    preStoreInit: (s) => {
      const surveyItem = s.navBarItems.find((x) => x.key === NavbarItemEnum.Survey);

      if (!surveyItem) {
        // add surveyItem before account
        const accountIndex = s.navBarItems.findIndex((x) => x.key === NavbarItemEnum.Account);
        s.navBarItems.splice(accountIndex, 0, { ...defaultSurveyNavbarItem });
      } else {
        // if already added, make sure it's before account
        const accountIndex = s.navBarItems.findIndex((x) => x.key === NavbarItemEnum.Account);
        const surveyIndex = s.navBarItems.findIndex((x) => x.key === NavbarItemEnum.Survey);
        if (surveyIndex > accountIndex) {
          s.navBarItems.splice(accountIndex, 0, s.navBarItems.splice(surveyIndex, 1)[0]);
        }
      }

      return s;
    },
  });

  isAppEmbedded$ = this.store.pipe(select((state) => state.launchSettings.isAppEmbedded));
  debug$ = this.store.pipe(select((s) => s.devSettings.debug));
  logs$ = this.store.pipe(select((s) => s.logs));
  newLogsCount$ = this.store.pipe(select((s) => s.logs.filter((x) => x.isNew).length));
  devOverlay$ = this.store.pipe(select((s) => s.devSettings.devOverlay));
  demoProxy$ = this.store.pipe(select((s) => s.devSettings.demoProxy));
  prefill$ = this.store.pipe(select((s) => s.devSettings.prefill));

  navBarPlace$ = this.store.pipe(select((s) => s.launchSettings.navBarPlace));
  navBarComputed$ = this.store.pipe(select((s) => s.navBarComputed));
  navBarItems$ = this.store.pipe(select((s) => s.navBarItems));
  notificationCount$ = this.store.pipe(select((s) => s.accountChips.notificationsCount));
  newsCount$ = this.store.pipe(select((s) => s.accountChips.newsCount));
  chatsCount$ = this.store.pipe(select((s) => s.accountChips.chatsCount));
  activeNavBarItems$ = this.store.pipe(select((s) => s.navBarItems.filter((x) => x.isActivated)));
  activeNavBarItemsForNavBar$ = this.store.pipe(select((s) => s.navBarItems.filter((x) => x.isActivated && x.isOnNavBar)));
  activeAccountNavBarItem$ = this.store.pipe(select((s) => s.navBarItems.find((x) => x.key === NavbarItemEnum.Account && x.isActivated)));
  accountNavBarItemNotif$ = this.store.pipe(select((s) => s.navBarItems.find((x) => x.key === NavbarItemEnum.Account)?.notif));
  reloadEnabledButNoSpaceOnNavBar$ = this.store.pipe(
    select((s) => {
      const reload = s.navBarItems.find((x) => x.key === NavbarItemEnum.Reload);
      return reload?.isActivated && !reload.isOnNavBar;
    }),
  );
  isFeatureActivated$ = (item: NavbarItemEnum) => {
    return this.store.pipe(select((s) => s.navBarItems.find((x) => x.key === item)?.isActivated));
  };

  appReactivatedAfterMinutes$ = this.store.pipe(
    filter((s) => !!s.lifecycle.pausedAt && !!s.lifecycle.resumedAt),
    select((s) => differenceInMinutes(ensureDate(s.lifecycle.resumedAt!), ensureDate(s.lifecycle.pausedAt!))),
  );

  getActiveNavBarItems = () => this.store.query((s) => s.navBarItems.filter((i) => i.isActivated));
  getDebug = () => this.store.query((s) => s.devSettings.debug);
  getSkipLinkAccount = () => this.store.query((s) => s.skipLinkAccount);
  getNavBarPlace = () => this.store.query((s) => s.launchSettings.navBarPlace);
  getNotificationsCount = () => this.store.query((s) => s.accountChips.notificationsCount);
  getChatsCount = () => this.store.query((s) => s.accountChips.chatsCount);
  getIsFeatureActivated = (item: NavbarItemEnum) => {
    return this.store.query((s) => s.navBarItems.find((x) => x.key === item)?.isActivated);
  };
  getFirstActivatedFeature = () => this.store.query((s) => s.navBarItems.find((x) => x.isActivated)?.key);

  toggleDebug = () => this.store.update((s) => ({ ...s, devSettings: { ...s.devSettings, debug: !s.devSettings.debug } }));
  togglePrefill = () => this.store.update((s) => ({ ...s, devSettings: { ...s.devSettings, prefill: !s.devSettings.prefill } }));
  toggleDevOverlay = () => this.store.update((s) => ({ ...s, devSettings: { ...s.devSettings, devOverlay: !s.devSettings.devOverlay } }));
  toggleDemoProxy = () => this.store.update((s) => ({ ...s, devSettings: { ...s.devSettings, demoProxy: !s.devSettings.demoProxy } }));

  toggleNavbarNotification(key: NavbarItemEnum) {
    this.store.update((s) => ({
      ...s,
      navBarItems: s.navBarItems.map((p) => (p.key === key ? { ...p, notif: p.notif ? null : { count: 1 } } : p)),
    }));
  }

  setDevOverlay = (isActive: boolean) => this.store.update((s) => ({ ...s, devSettings: { ...s.devSettings, devOverlay: isActive } }));
  setDemoProxy = (isActive: boolean) => this.store.update((s) => ({ ...s, devSettings: { ...s.devSettings, demoProxy: isActive } }));
  setPrefill = (isActive: boolean) => this.store.update((s) => ({ ...s, devSettings: { ...s.devSettings, prefill: isActive } }));

  getStoreSnapshot() {
    return this.store.query((s) => s);
  }

  setChatsCount(count: number) {
    const totalCount = this.store.query((s) => s.accountChips.newsCount + s.accountChips.notificationsCount) + count;
    this.store.update((s) => ({
      ...s,
      accountChips: { ...s.accountChips, chatsCount: count },
      navBarItems: s.navBarItems.map((p) => (p.key === NavbarItemEnum.Account ? { ...p, notif: totalCount === 0 ? null : { count: totalCount } } : p)),
    }));
  }

  setNotificationsCount(count: number) {
    const totalCount = this.store.query((s) => s.accountChips.newsCount + (s.accountChips?.chatsCount ?? 0)) + count;
    this.store.update((s) => ({
      ...s,
      accountChips: { ...s.accountChips, notificationsCount: count },
      navBarItems: s.navBarItems.map((p) => (p.key === NavbarItemEnum.Account ? { ...p, notif: totalCount === 0 ? null : { count: totalCount } } : p)),
    }));
  }

  setNewsCount(count: number) {
    const totalCount = this.store.query((s) => s.accountChips.notificationsCount + (s.accountChips?.chatsCount ?? 0)) + count;
    this.store.update((s) => ({
      ...s,
      accountChips: { ...s.accountChips, newsCount: count },
      navBarItems: s.navBarItems.map((p) => (p.key === NavbarItemEnum.Account ? { ...p, notif: totalCount === 0 ? null : { count: totalCount } } : p)),
    }));
  }

  setNavbarItemConfig(key: NavbarItemEnum, isActivated: boolean, isOnNavBar: boolean) {
    this.store.update((s) => ({
      ...s,
      navBarItems: s.navBarItems.map((p) => (p.key === key ? { ...p, isActivated, isOnNavBar } : p)),
    }));
  }

  setNavbarComputed() {
    this.store.update((s) => ({
      ...s,
      navBarComputed: true,
    }));
  }

  setAppEmbedded(isEmbedded: boolean) {
    this.store.update((s) => ({ ...s, launchSettings: { ...s.launchSettings, isAppEmbedded: isEmbedded } }));
  }

  setNavBarPlace(navBarPlace: NavbarPosition) {
    this.store.update((s) => ({ ...s, launchSettings: { ...s.launchSettings, navBarPlace: navBarPlace } }));
  }

  setSkipLinkAccount(skipLinkAccount: boolean) {
    this.store.update((s) => ({ ...s, skipLinkAccount }));
  }

  setAppResumed(date: Date) {
    this.store.update((s) => ({ ...s, lifecycle: { pausedAt: s.lifecycle.pausedAt, resumedAt: date } }));
  }

  setAppPaused(date: Date) {
    this.store.update((s) => ({ ...s, lifecycle: { resumedAt: null, pausedAt: date } }));
  }

  pushLog(log: LogEntry) {
    this.store.update((s) => ({ ...s, logs: [{ ...log, isNew: true }, ...s.logs].slice(0, MAX_LOGS) }));
  }

  clearLogs() {
    this.store.update((s) => ({ ...s, logs: [] }));
  }

  setLogsAsShown() {
    this.store.update((s) => ({ ...s, logs: s.logs.map((l) => ({ ...l, isNew: false })) }));
  }
}
